import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControl,
  FormControlLabel,
  GlobalStyles,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { GoogleMap, InfoWindow, OverlayView, useLoadScript } from '@react-google-maps/api'
import dayjs from 'dayjs'
import {
  CalendarDaysIcon,
  ImageOffIcon,
  MapIcon,
  MapPinIcon,
  SearchIcon,
  StarIcon
} from 'lucide-react'
import Link from 'next/link'

import Button from '@/components/base/button'
import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks'
import { useLocaleStore } from '@/store/locale'
import { useWishlistStore } from '@/store/wishlist'
import { formatPrice } from '@/utils/strings'

import 'dayjs/locale/it'

import { useAdvertisementsSearch } from '@/api/advertisements'
import { Advertisement, AdvertisementSearchParams } from '@/api/advertisements/types'
import { useCities } from '@/api/cities'
import { useFeatures } from '@/api/features'
import { FeatureGroup } from '@/api/features/types'
import { City } from '@/api/types'

const GOOGLE_MAPS_LIBRARIES: 'places'[] = ['places']

const DEFAULT_ZOOM = 14

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  gestureHandling: 'greedy'
}

// Default center: Italy
const DEFAULT_CENTER = { lat: 44.49673719550812, lng: 11.332443274734215 }

/**
 * Custom HTML Pin Marker Component
 */
const PinMarker = memo(
  ({ text, isSelected, onClick }: { text: string; isSelected: boolean; onClick: () => void }) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      onClick()
    }

    return (
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          transform: 'translate(-50%, -100%)',
          transition: 'transform 0.15s ease',
          '&:hover': {
            transform: 'translate(-50%, -100%) scale(1.1)'
          }
        }}
      >
        {/* Price/Count label */}
        <Box
          sx={{
            bgcolor: isSelected ? 'primary.main' : 'background.paper',
            color: isSelected ? 'primary.contrastText' : 'text.primary',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            boxShadow: 2,
            fontWeight: 600,
            fontSize: '12px',
            whiteSpace: 'nowrap',
            border: isSelected ? 'none' : '1px solid',
            borderColor: 'divider'
          }}
        >
          {text}
        </Box>
        {/* Pin arrow */}
        <Box
          sx={{
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: isSelected ? '8px solid' : '8px solid',
            borderTopColor: isSelected ? 'primary.main' : 'background.paper',
            mt: '-1px',
            filter: isSelected ? 'none' : 'drop-shadow(0 1px 1px rgba(0,0,0,0.15))'
          }}
        />
      </Box>
    )
  }
)

PinMarker.displayName = 'PinMarker'

// Map styles for a cleaner look
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'transit',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }]
  }
]

type MapBounds = {
  ne: { lat: number; lng: number }
  sw: { lat: number; lng: number }
}

type OrderByValue = 'price' | '-price' | 'date' | '-date'

type MapFilters = {
  city_id?: number
  min_price?: number
  max_price?: number
  min_deposit?: number
  max_deposit?: number
  min_area?: number
  max_area?: number
  min_bedrooms?: number
  min_bathrooms?: number
  min_floor?: number
  max_floor?: number
  min_contract_months?: number
  max_contract_months?: number
  min_available_places?: number
  available_from?: string
  available_to?: string
  features?: string[]
  order_by?: OrderByValue
}

type MapSearchModalProps = {
  open: boolean
  onClose: () => void
  initialFilters?: Partial<AdvertisementSearchParams>
}

/**
 * Price marker component
 */
const PriceMarker = memo(
  ({
    advertisement,
    isSelected,
    onClick
  }: {
    advertisement: Advertisement
    isSelected: boolean
    onClick: () => void
  }) => {
    const price = formatPrice(advertisement.price)

    return (
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isSelected ? 'primary.main' : 'background.paper',
          color: isSelected ? 'primary.contrastText' : 'text.primary',
          borderRadius: '20px',
          padding: '4px 10px',
          fontWeight: 600,
          fontSize: '13px',
          boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.15)',
          border: isSelected ? 'none' : '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          transform: isSelected ? 'scale(1.1)' : 'scale(1)',
          transition: 'all 0.2s ease',
          zIndex: isSelected ? 100 : 1,
          '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: isSelected ? '6px solid' : '6px solid',
            borderTopColor: isSelected ? 'primary.main' : 'background.paper'
          }
        }}
      >
        {price}
      </Box>
    )
  }
)

PriceMarker.displayName = 'PriceMarker'

/**
 * Advertisement card for the list
 */
const MapAdvertisementCard = memo(
  ({
    advertisement,
    isSelected,
    onClick,
    onWishlistToggle
  }: {
    advertisement: Advertisement
    isSelected: boolean
    onClick: () => void
    onWishlistToggle: () => void
  }) => {
    const currentLocale = useLocaleStore((state) => state.currentLocale)
    const { strings } = useAppStore()
    const { items: wishlistItems } = useWishlistStore()
    const isInWishlist = wishlistItems.some((item) => item.id === advertisement.id)
    const [imageError, setImageError] = useState(false)

    const image = advertisement.attachments?.[0]
    const title = advertisement.title?.[currentLocale!] || 'Annuncio'
    const city = advertisement.property?.city?.name?.[currentLocale!] || ''
    const isBoosted = !!(
      advertisement.boost_valid_until && new Date(advertisement.boost_valid_until) > new Date()
    )
    const price = formatPrice(advertisement.price)
    const features = [
      ...(advertisement.features || []),
      ...(advertisement.property?.features || [])
    ]
      .filter((f) => f.group?.slug === 'struct_type')
      .map((f) => f.name?.[currentLocale!])
      .filter(Boolean)
      .join(' • ')

    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          gap: 2,
          p: 1.5,
          borderRadius: (theme) => theme.shape.borderRadius2,
          cursor: 'pointer',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          boxShadow: `0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.08)`,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
      >
        {/* Image */}
        <Box
          sx={{
            position: 'relative',
            width: 100,
            borderRadius: (theme) => theme.shape.borderRadius2,
            overflow: 'hidden',
            aspectRatio: '1/1',
            flexShrink: 0,
            bgcolor: 'grey.200'
          }}
        >
          {image && !imageError ? (
            <Box
              component="img"
              src={`/images/${image.thumb_path || image.path}`}
              alt={title}
              onError={() => setImageError(true)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ImageOffIcon size={32} style={{ color: 'rgba(0, 0, 0, 0.3)' }} />
            </Box>
          )}
          <IconButton
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onWishlistToggle()
            }}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'background.paper'
              }
            }}
            size="small"
          >
            <SvgIcon
              sx={{ color: isInWishlist ? 'error.main' : 'text.secondary', fontSize: '1rem' }}
            >
              <use href={isInWishlist ? '#ico-heart-filled' : '#ico-heart'} />
            </SvgIcon>
          </IconButton>

          {isBoosted && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: 'warning.main',
                boxShadow: 1
              }}
            >
              <StarIcon size={12} fill="#fff" color="#fff" />
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {city}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            component={Link}
            href={`/advertisements/${advertisement.id}`}
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {title}
          </Typography>
          {features && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {features}
            </Typography>
          )}
          <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ mt: 0.5 }}>
            {price}/{strings?.labels?.month}
          </Typography>
        </Box>
      </Box>
    )
  }
)

MapAdvertisementCard.displayName = 'MapAdvertisementCard'

// Price options for filters
const PRICE_OPTIONS = [
  { value: '', label: '' },
  { value: '300', label: '€300' },
  { value: '400', label: '€400' },
  { value: '500', label: '€500' },
  { value: '600', label: '€600' },
  { value: '700', label: '€700' },
  { value: '800', label: '€800' },
  { value: '900', label: '€900' },
  { value: '1000', label: '€1000+' }
]

/**
 * Filters panel for the map view (same filters as search page)
 */
const MapFiltersPanel = memo(
  ({
    filters,
    features,
    isFeaturesLoading,
    onFiltersChange
  }: {
    filters: MapFilters
    features: Record<string, FeatureGroup> | undefined
    isFeaturesLoading: boolean
    onFiltersChange: (_filters: Partial<MapFilters>) => void
  }) => {
    const { t } = useTranslation('common')
    const currentLocale = useLocaleStore((state) => state.currentLocale)
    const { collection: cities } = useCities({ page: 0, per_page: 100, onlyWithAdvertisements: true })
    const [isMounted, setIsMounted] = useState(false)
    const { strings } = useAppStore()

    useEffect(() => {
      setIsMounted(true)
    }, [])

    const handleSelectChange = useCallback(
      (key: keyof MapFilters) => (event: SelectChangeEvent<string>) => {
        const value = event.target.value ? parseInt(event.target.value, 10) : undefined
        onFiltersChange({ [key]: value })
      },
      [onFiltersChange]
    )

    const handleFeatureToggle = useCallback(
      (slug: string, checked: boolean) => {
        const currentFeatures = filters.features || []
        const newFeatures = checked
          ? [...currentFeatures, slug]
          : currentFeatures.filter((f) => f !== slug)
        onFiltersChange({ features: newFeatures.length > 0 ? newFeatures : undefined })
      },
      [filters.features, onFiltersChange]
    )

    const handleFeatureGroupChange = useCallback(
      (groupSlugs: string[], selectedSlug: string | null) => {
        const currentFeatures = filters.features || []
        const filteredFeatures = currentFeatures.filter((f) => !groupSlugs.includes(f))
        const newFeatures = selectedSlug ? [...filteredFeatures, selectedSlug] : filteredFeatures
        onFiltersChange({ features: newFeatures.length > 0 ? newFeatures : undefined })
      },
      [filters.features, onFiltersChange]
    )

    const HIDDEN_FEATURE_SLUGS = [
      'private-bathroom',
      'mixed_room',
      'induction_cooktop',
      'induction-cooktop',
      'has_windows'
    ]

    const getFeatureGroup = (slug: string) => {
      if (isFeaturesLoading || !features) return null
      const group = features[slug]
      if (!group) return null
      const filteredGroup =
        slug === 'ungrouped'
          ? {
              ...group,
              features: group.features.filter((f) => !HIDDEN_FEATURE_SLUGS.includes(f.slug))
            }
          : group

      const isUngrouped = slug === 'ungrouped'
      const groupSlugs = filteredGroup.features.map((f) => f.slug).filter((s): s is string => !!s)
      const selectedInGroup = groupSlugs.find((s) => filters.features?.includes(s)) || ''

      if (isUngrouped) {
        return (
          <Box key={slug} sx={{ display: 'grid', gap: 1 }}>
            <Typography
              variant="textRegular"
              fontWeight="500"
              color="text.secondaryContrastText"
              sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
            >
              {filteredGroup.name?.it || strings?.labels?.features}
            </Typography>
            {filteredGroup.features.map((feature) => {
              const featureSlug = feature.slug
              if (!featureSlug) return null
              return (
                <FormControlLabel
                  key={featureSlug}
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.features?.includes(featureSlug) || false}
                      onChange={(e) => handleFeatureToggle(featureSlug, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="textSmall">
                      {feature.name?.[currentLocale!] || featureSlug}
                    </Typography>
                  }
                />
              )
            })}
          </Box>
        )
      }

      return (
        <Box key={slug} sx={{ display: 'grid', gap: 2 }}>
          <Typography
            variant="textRegular"
            fontWeight="500"
            color="text.secondaryContrastText"
            sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
          >
            {filteredGroup.name?.[currentLocale!] || 'Caratteristiche'}
          </Typography>
          <FormControl size="small">
            <Select
              value={selectedInGroup}
              onChange={(e) => {
                handleFeatureGroupChange(groupSlugs, e.target.value || null)
              }}
              variant="standard"
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return `${strings?.labels?.select}...`
                const feature = filteredGroup.features.find((f) => f.slug === selected)
                return feature?.name?.[currentLocale!] || selected
              }}
              sx={{
                borderRadius: (theme) => theme.shape.borderRadius2,
                '& .MuiSelect-select': { py: 1 }
              }}
            >
              <MenuItem value="">
                <em>{strings?.labels?.any}</em>
              </MenuItem>
              {filteredGroup.features.map((feature) => {
                const featureSlug = feature.slug
                if (!featureSlug) return null
                return (
                  <MenuItem key={featureSlug} value={featureSlug}>
                    {feature.name?.[currentLocale!] || featureSlug}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
      )
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gap: 4,
          padding: (theme) => theme.spacing(2, 2, 3),
          boxShadow: (theme) => theme.shadows[2],
          borderRadius: (theme) => theme.shape.borderRadius4,
          overflowY: 'auto',
          height: '100%',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        {/* Order by */}
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography
            variant="textLarge"
            fontWeight={isHappa ? '700' : '500'}
            color="text.secondaryContrastText"
            sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
          >
            {strings?.labels?.order_by || 'Ordina per:'}
          </Typography>
          <FormControl size="small">
            <Select
              value={filters.order_by || '-date'}
              onChange={(e) => onFiltersChange({ order_by: e.target.value as OrderByValue })}
              variant="standard"
              displayEmpty
              sx={{
                borderRadius: (theme) => theme.shape.borderRadius2,
                '& .MuiSelect-select': { py: 1 }
              }}
            >
              <MenuItem value="-date">{strings?.labels?.most_recent || 'Più recenti'}</MenuItem>
              <MenuItem value="date">{strings?.labels?.least_recent || 'Meno recenti'}</MenuItem>
              <MenuItem value="price">
                {strings?.labels?.price_ascending || 'Prezzo crescente'}
              </MenuItem>
              <MenuItem value="-price">
                {strings?.labels?.price_descending || 'Prezzo decrescente'}
              </MenuItem>
              <MenuItem value="available_from">
                {strings?.labels?.availability || 'Disponibilità'}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography
          variant="textLarge"
          fontWeight={isHappa ? '700' : '500'}
          color="text.secondaryContrastText"
          sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
        >
          {strings?.labels?.filter_by || 'Filtra per:'}
        </Typography>

        {/* 1. Tipo sistemazione */}
        {getFeatureGroup('accommodation_type')}

        {/* 2. Ospiti in stanza */}
        {getFeatureGroup('guests')}

        {/* 3. Canone mensile */}
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography
            variant="textRegular"
            fontWeight="500"
            color="text.secondaryContrastText"
            sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
          >
            {strings?.labels?.monthly_rent || 'Canone mensile'}
          </Typography>
          <Stack direction="row" spacing={4} alignItems="center">
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={filters.min_price?.toString() ?? ''}
                onChange={handleSelectChange('min_price')}
                variant="standard"
                displayEmpty
                sx={{
                  borderRadius: (theme) => theme.shape.borderRadius2,
                  '& .MuiSelect-select': { py: 1 }
                }}
              >
                {PRICE_OPTIONS.map((opt) => (
                  <MenuItem key={`min-${opt.value}`} value={opt.value}>
                    {opt.value ? opt.label : strings?.labels?.any}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={filters.max_price?.toString() ?? ''}
                onChange={handleSelectChange('max_price')}
                variant="standard"
                displayEmpty
                sx={{
                  borderRadius: (theme) => theme.shape.borderRadius2,
                  '& .MuiSelect-select': { py: 1 }
                }}
              >
                {PRICE_OPTIONS.map((opt) => (
                  <MenuItem key={`max-${opt.value}`} value={opt.value}>
                    {opt.value ? opt.label : strings?.labels?.any}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* 4. Disponibile dal */}
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography
            variant="textRegular"
            fontWeight="500"
            color="text.secondaryContrastText"
            sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
          >
            {strings?.labels?.available_from || 'Disponibile dal'}
          </Typography>
          {isMounted ? (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocale || 'it'}>
              <DatePicker
                value={filters.available_from ? dayjs(filters.available_from) : null}
                onChange={(newValue) => {
                  onFiltersChange({
                    available_from: newValue ? newValue.format('YYYY-MM-DD') : undefined
                  })
                }}
                slots={{
                  openPickerIcon: () => <CalendarDaysIcon size={16} />
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'standard',
                    sx: {
                      borderRadius: (theme) => theme.shape.borderRadius2,
                      '& .MuiInputBase-root': { py: 1 }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          ) : (
            <TextField size="small" variant="standard" disabled placeholder="Seleziona data" />
          )}
        </Box>

        {/* 5. Disponibile fino al */}
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography
            variant="textRegular"
            fontWeight="500"
            color="text.secondaryContrastText"
            sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
          >
            {strings?.labels?.available_until || 'Disponibile fino al'}
          </Typography>
          {isMounted ? (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocale || 'it'}>
              <DatePicker
                value={filters.available_to ? dayjs(filters.available_to) : null}
                onChange={(newValue) => {
                  onFiltersChange({
                    available_to: newValue ? newValue.format('YYYY-MM-DD') : undefined
                  })
                }}
                slots={{
                  openPickerIcon: () => <CalendarDaysIcon size={16} />
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'standard',
                    sx: {
                      borderRadius: (theme) => theme.shape.borderRadius2,
                      '& .MuiInputBase-root': { py: 1 }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          ) : (
            <TextField size="small" variant="standard" disabled placeholder="Seleziona data" />
          )}
        </Box>

        {/* 6. Tipo immobile */}
        {getFeatureGroup('struct_type')}

        {/* 7. Piano */}
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography
            variant="textRegular"
            fontWeight="500"
            color="text.secondaryContrastText"
            sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
          >
            {strings?.labels?.floor || 'Piano'}
          </Typography>
          <Stack direction="row" spacing={4} alignItems="center">
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={filters.min_floor?.toString() ?? ''}
                onChange={handleSelectChange('min_floor')}
                variant="standard"
                displayEmpty
                sx={{
                  borderRadius: (theme) => theme.shape.borderRadius2,
                  '& .MuiSelect-select': { py: 1 }
                }}
              >
                <MenuItem value="">{strings?.labels?.any || 'Qualsiasi'}</MenuItem>
                <MenuItem value="0">{strings?.labels?.ground_floor || 'Piano terra'}</MenuItem>
                <MenuItem value="1">1°</MenuItem>
                <MenuItem value="2">2°</MenuItem>
                <MenuItem value="3">3°</MenuItem>
                <MenuItem value="4">4°</MenuItem>
                <MenuItem value="5">5°+</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={filters.max_floor?.toString() ?? ''}
                onChange={handleSelectChange('max_floor')}
                variant="standard"
                displayEmpty
                sx={{
                  borderRadius: (theme) => theme.shape.borderRadius2,
                  '& .MuiSelect-select': { py: 1 }
                }}
              >
                <MenuItem value="">{strings?.labels?.any || 'Qualsiasi'}</MenuItem>
                <MenuItem value="0">{strings?.labels?.ground_floor || 'Piano terra'}</MenuItem>
                <MenuItem value="1">1°</MenuItem>
                <MenuItem value="2">2°</MenuItem>
                <MenuItem value="3">3°</MenuItem>
                <MenuItem value="4">4°</MenuItem>
                <MenuItem value="5">5°+</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* 8. Caratteristiche */}
        {getFeatureGroup('ungrouped')}

        {/* 9. Distanza trasporti pubblici */}
        {getFeatureGroup('distance_transp')}

        {/* 10. Proprietario residente */}
        {getFeatureGroup('owner_resident')}
      </Box>
    )
  }
)

MapFiltersPanel.displayName = 'MapFiltersPanel'

/**
 * Places Autocomplete Component for geocoding search
 */
type PlacesAutocompleteProps = {
  onPlaceSelect: (_lat: number, _lng: number) => void
}

const PlacesAutocomplete = memo(({ onPlaceSelect }: PlacesAutocompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)
  const [inputValue, setInputValue] = useState('')
  const { strings } = useAppStore()

  useEffect(() => {
    if (!inputRef.current || autocompleteRef.current) return

    // Initialize geocoder
    geocoderRef.current = new google.maps.Geocoder()

    autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['geocode', 'establishment'],
      componentRestrictions: { country: 'it' },
      fields: ['geometry', 'formatted_address', 'name']
    })

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current?.getPlace()
      if (place?.geometry?.location) {
        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        onPlaceSelect(lat, lng)
        setInputValue(place.formatted_address || place.name || '')
      }
    })

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
    }
  }, [onPlaceSelect])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (!inputValue.trim() || !geocoderRef.current) return

        geocoderRef.current.geocode(
          {
            address: inputValue,
            componentRestrictions: { country: 'it' }
          },
          (results, status) => {
            if (status === 'OK' && results && results[0]?.geometry?.location) {
              const location = results[0].geometry.location
              onPlaceSelect(location.lat(), location.lng())
              setInputValue(results[0].formatted_address || inputValue)
            }
          }
        )
      }
    },
    [inputValue, onPlaceSelect]
  )

  return (
    <>
      {/* Global style to ensure Google Places dropdown appears above Drawer */}
      <GlobalStyles
        styles={{
          '.pac-container': {
            zIndex: '1500 !important'
          }
        }}
      />
      <TextField
        inputRef={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={strings?.labels?.search_address}
        size="small"
        fullWidth
        slotProps={{
          input: {
            startAdornment: (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                <SearchIcon size={18} />
              </Box>
            ),
            endAdornment: inputValue && (
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                <MapPinIcon size={16} />
              </Box>
            )
          }
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.paper',
            borderRadius: (theme) => theme.shape.borderRadius360,
            height: '4.8rem'
          }
        }}
      />
    </>
  )
})

PlacesAutocomplete.displayName = 'PlacesAutocomplete'

/**
 * Main Map Search Modal Component
 */
const MapSearchModal = ({ open, onClose, initialFilters }: MapSearchModalProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const { toggleItem } = useWishlistStore()
  const { strings } = useAppStore()

  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null)
  const [selectedAdvertisementId, setSelectedAdvertisementId] = useState<number | null>(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null)
  const [showList, setShowList] = useState(!isMobile)
  const [filters, setFilters] = useState<MapFilters>({
    city_id: initialFilters?.city_id,
    min_price: initialFilters?.min_price,
    max_price: initialFilters?.max_price,
    features: initialFilters?.features
  })

  const mapRef = useRef<google.maps.Map | null>(null)

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES
  })

  // Fetch features for filters
  const { features, isLoading: isFeaturesLoading } = useFeatures()

  // When isHappa and city filter changes, pan/zoom map to selected city
  const { collection: cities } = useCities({ page: 0, per_page: 100, onlyWithAdvertisements: true })
  useEffect(() => {
    if (!isHappa || !filters.city_id || !isLoaded || !mapRef.current) return
    const city = (cities || []).find((c: City) => c.id === filters.city_id)
    if (!city) return
    const cityName = city.name?.[currentLocale || 'it'] || city.name?.it || city.name?.en
    if (!cityName) return
    const geocoder = new google.maps.Geocoder()
    geocoder.geocode(
      { address: cityName, componentRestrictions: { country: 'it' } },
      (results, status) => {
        if (status !== 'OK' || !results?.[0]) return
        const { geometry } = results[0]
        if (geometry.viewport) {
          mapRef.current?.fitBounds(geometry.viewport)
        } else if (geometry.location) {
          mapRef.current?.panTo(geometry.location)
          mapRef.current?.setZoom(12)
        }
      }
    )
  }, [filters.city_id, isLoaded, cities, currentLocale])

  // Build search params including map bounds
  const searchParams = useMemo<AdvertisementSearchParams>(() => {
    const params: AdvertisementSearchParams = {
      per_page: 100, // Load more for map view
      ...filters
    }

    // Add bounding box if available
    // Format: bounding_box[0][]=[ne.lat, ne.lng], bounding_box[1][]=[sw.lat, sw.lng]
    if (mapBounds) {
      params.bounding_box = [
        [mapBounds.ne.lat, mapBounds.ne.lng],
        [mapBounds.sw.lat, mapBounds.sw.lng]
      ]
    }

    return params
  }, [mapBounds, filters])

  const { collection, isLoading } = useAdvertisementsSearch(open ? searchParams : undefined)

  // Filter advertisements that have valid coordinates
  const advertisementsWithCoords = useMemo(() => {
    if (!collection) return []
    return collection.filter((ad) => {
      const lat = ad.property?.latitude
      const lng = ad.property?.longitude
      return lat != null && lng != null && !isNaN(lat) && !isNaN(lng)
    })
  }, [collection])

  // Group advertisements by property ID for markers
  const advertisementsByProperty = useMemo(() => {
    const grouped = new Map<number, Advertisement[]>()
    advertisementsWithCoords.forEach((ad) => {
      const propertyId = ad.property?.id
      if (propertyId != null) {
        const existing = grouped.get(propertyId) || []
        grouped.set(propertyId, [...existing, ad])
      }
    })
    return grouped
  }, [advertisementsWithCoords])

  // Handle map load
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map

    // Set initial bounds after map loads
    const bounds = map.getBounds()
    if (bounds) {
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      setMapBounds({
        ne: { lat: ne.lat(), lng: ne.lng() },
        sw: { lat: sw.lat(), lng: sw.lng() }
      })
    }
  }, [])

  // Threshold for bounds change detection (in degrees, ~100m at equator)
  const BOUNDS_THRESHOLD = 0.001

  // Check if bounds changed significantly
  const haveBoundsChangedSignificantly = useCallback(
    (newBounds: MapBounds) => {
      if (!mapBounds) return true
      return (
        Math.abs(newBounds.ne.lat - mapBounds.ne.lat) > BOUNDS_THRESHOLD ||
        Math.abs(newBounds.ne.lng - mapBounds.ne.lng) > BOUNDS_THRESHOLD ||
        Math.abs(newBounds.sw.lat - mapBounds.sw.lat) > BOUNDS_THRESHOLD ||
        Math.abs(newBounds.sw.lng - mapBounds.sw.lng) > BOUNDS_THRESHOLD
      )
    },
    [mapBounds]
  )

  // Handle map idle - fires only when map becomes idle after any interaction
  const onIdle = useCallback(() => {
    if (!mapRef.current) return

    const bounds = mapRef.current.getBounds()
    if (bounds) {
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const newBounds: MapBounds = {
        ne: { lat: ne.lat(), lng: ne.lng() },
        sw: { lat: sw.lat(), lng: sw.lng() }
      }

      // Only update if bounds changed significantly to avoid unnecessary API calls
      if (haveBoundsChangedSignificantly(newBounds)) {
        setMapBounds(newBounds)
      }
    }
  }, [haveBoundsChangedSignificantly])

  // Handle marker click
  const handleMarkerClick = useCallback(
    (advertisementId: number) => {
      setSelectedAdvertisementId(advertisementId)
      if (isMobile) {
        setShowList(true)
      }
    },
    [isMobile]
  )

  // Handle card click - center map on marker
  const handleCardClick = useCallback((advertisement: Advertisement) => {
    const lat = advertisement.property?.latitude
    const lng = advertisement.property?.longitude

    if (lat != null && lng != null && mapRef.current) {
      mapRef.current.panTo({ lat, lng })
      setSelectedAdvertisementId(advertisement.id)
    }
  }, [])

  // Handle filters change
  const handleFiltersChange = useCallback((newFilters: Partial<MapFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
  }, [])

  // Handle place selection from autocomplete
  const handlePlaceSelect = useCallback((lat: number, lng: number) => {
    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng })
      mapRef.current.setZoom(15)
    }
  }, [])

  // Scroll to selected card
  const listRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (selectedAdvertisementId && listRef.current) {
      const selectedCard = listRef.current.querySelector(
        `[data-ad-id="${selectedAdvertisementId}"]`
      )
      if (selectedCard) {
        selectedCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [selectedAdvertisementId])

  const handleWishlistToggle = useCallback(
    (id: number) => {
      const model = advertisementsWithCoords?.find((m) => m.id === id)
      if (model) {
        toggleItem({
          id: model.id,
          title: model?.title?.[currentLocale!] || ''
        })
      }
    },
    [advertisementsWithCoords, currentLocale, toggleItem]
  )

  if (loadError) {
    return null
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          pt: { xs: '1.6rem', md: '3.2rem' },
          px: { xs: '1.6rem', md: '3.2rem' }
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Main content - three column layout: Map | Results | Filters */}
        <Box
          sx={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden', gap: '3.2rem' }}
        >
          {/* Results list panel */}
          <Box
            ref={listRef}
            sx={{
              width: { xs: showList ? '100%' : 0, md: 340 },
              position: { xs: 'absolute', md: 'relative' },
              backgroundColor: 'background.paper',
              top: 0,
              right: { xs: 0, md: 'auto' },
              height: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              transition: 'width 0.3s ease',
              zIndex: 10,
              order: 0,
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }}
          >
            {/* Mobile close button for list */}
            {isMobile && showList && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                <IconButton size="medium" color="primary" onClick={() => setShowList(false)}>
                  <SvgIcon>
                    <use href="#ico-close" />
                  </SvgIcon>
                </IconButton>
              </Stack>
            )}

            {/* List content */}
            <Stack spacing={2}>
              {advertisementsWithCoords.length === 0 && !isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <MapIcon size={48} style={{ opacity: 0.3 }} />
                  <Typography variant="textRegular" color="text.secondary" mt={2}>
                    {strings?.labels?.no_ads_found_in_this_area}
                  </Typography>
                  <Typography variant="textSmall" color="text.secondary" mt={1}>
                    {strings?.labels?.try_moving_map_or_changing_filters}
                  </Typography>
                </Box>
              ) : (
                advertisementsWithCoords.map((ad) => (
                  <Box key={ad.id} data-ad-id={ad.id}>
                    <MapAdvertisementCard
                      advertisement={ad}
                      isSelected={selectedAdvertisementId === ad.id}
                      onClick={() => handleCardClick(ad)}
                      onWishlistToggle={() => handleWishlistToggle(ad.id)}
                    />
                  </Box>
                ))
              )}
            </Stack>
          </Box>

          {/* Filters panel */}
          <Box
            sx={{
              width: { xs: 0, md: 300 },
              display: { xs: 'none', md: 'block' },
              height: '100%',
              pb: '3.2rem'
            }}
          >
            <MapFiltersPanel
              filters={filters}
              features={features}
              isFeaturesLoading={isFeaturesLoading}
              onFiltersChange={handleFiltersChange}
            />
          </Box>

          {/* Map */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              minHeight: 0,
              borderRadius: theme.shape.borderRadius4,
              overflow: 'hidden',
              mb: { xs: '1.6rem', md: '3.2rem' },
              order: { xs: 0, md: 0 }
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={onClose}
              icon="close"
              sx={{
                position: 'absolute',
                zIndex: 1,
                top: { xs: '0.8rem', md: '1.6rem' },
                right: { xs: '0.8rem', md: '1.6rem' },
                display: { xs: 'none', md: 'flex' }
              }}
            >
              {strings?.labels?.close_map}
            </Button>
            <IconButton
              size="medium"
              color="primary"
              onClick={onClose}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: { md: 'none' } }}
            >
              <SvgIcon>
                <use href="#ico-close" />
              </SvgIcon>
            </IconButton>

            {/* Geocoding search bar */}
            {isLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: '0.8rem', md: '1.6rem' },
                  left: { xs: '0.8rem', md: '1.6rem' },
                  zIndex: 1,
                  width: { xs: 'calc(100% - 5.6rem)', md: 320 },
                  maxWidth: 400
                }}
              >
                <PlacesAutocomplete onPlaceSelect={handlePlaceSelect} />
              </Box>
            )}

            {!isLoaded ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                options={{
                  ...MAP_OPTIONS,
                  styles: MAP_STYLES
                }}
                onLoad={onMapLoad}
                onIdle={onIdle}
              >
                {/* Render markers grouped by property */}
                {Array.from(advertisementsByProperty.entries()).map(([propertyId, ads]) => {
                  const firstAd = ads[0]
                  const lat = firstAd.property?.latitude
                  const lng = firstAd.property?.longitude

                  if (lat == null || lng == null) return null

                  const hasMultipleAds = ads.length > 1
                  const isPropertySelected = selectedPropertyId === propertyId
                  const isAnyAdSelected = ads.some((ad) => ad.id === selectedAdvertisementId)
                  const markerText = hasMultipleAds ? `${ads.length}` : formatPrice(firstAd.price)

                  return (
                    <OverlayView
                      key={propertyId}
                      position={{ lat, lng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <PinMarker
                        text={markerText}
                        isSelected={isPropertySelected || isAnyAdSelected}
                        onClick={() => {
                          setSelectedPropertyId((prev) => (prev === propertyId ? null : propertyId))
                          if (!hasMultipleAds) {
                            handleMarkerClick(firstAd.id)
                          }
                        }}
                      />
                    </OverlayView>
                  )
                })}

                {/* InfoWindow for selected property */}
                {(() => {
                  const propertyAds = selectedPropertyId
                    ? advertisementsByProperty.get(selectedPropertyId)
                    : null
                  const firstAd = propertyAds?.[0]
                  const lat = firstAd?.property?.latitude
                  const lng = firstAd?.property?.longitude

                  if (!propertyAds || lat == null || lng == null) return null

                  const hasMultipleAds = propertyAds.length > 1

                  return (
                    <InfoWindow
                      position={{ lat, lng }}
                      onCloseClick={() => setSelectedPropertyId(null)}
                      options={{
                        pixelOffset: new google.maps.Size(0, -30)
                      }}
                    >
                      <Box sx={{ maxWidth: 280, maxHeight: 300, overflow: 'auto' }}>
                        {hasMultipleAds ? (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              {propertyAds.length} {strings?.labels?.ads_in_this_property}
                            </Typography>
                            <Stack spacing={1}>
                              {propertyAds.map((ad) => (
                                <Box
                                  key={ad.id}
                                  onClick={() => {
                                    handleMarkerClick(ad.id)
                                    setSelectedPropertyId(null)
                                  }}
                                  sx={{
                                    p: 1,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' }
                                  }}
                                >
                                  <Typography variant="textRegular" fontWeight={600}>
                                    {ad.title?.[currentLocale!] || `Annuncio #${ad.id}`}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatPrice(ad.price)}/{strings?.labels?.month}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {ad.property?.area ? `${ad.property.area} m²` : ''}
                                    {ad.property?.area && ad.property?.bedrooms ? ' • ' : ''}
                                    {ad.property?.bedrooms ? `${ad.property.bedrooms} camere` : ''}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </>
                        ) : firstAd ? (
                          <Box
                            onClick={() => {
                              handleMarkerClick(firstAd.id)
                              setSelectedPropertyId(null)
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Typography variant="textRegular" fontWeight={600}>
                              {firstAd.title?.[currentLocale!] || `Annuncio #${firstAd.id}`}
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {formatPrice(firstAd.price)}/{strings?.labels?.month}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {firstAd.property?.area ? `${firstAd.property.area} m²` : ''}
                              {firstAd.property?.area && firstAd.property?.bedrooms ? ' • ' : ''}
                              {firstAd.property?.bedrooms
                                ? `${firstAd.property.bedrooms} camere`
                                : ''}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="primary"
                              sx={{ mt: 0.5, display: 'block' }}
                              component="a"
                              href={`/advertisements/${firstAd.id}`}
                            >
                              {strings?.labels?.view_details}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    </InfoWindow>
                  )
                })()}
              </GoogleMap>
            )}

            {/* Loading overlay */}
            {isLoading && isLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  boxShadow: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2">{strings?.labels?.loading}</Typography>
              </Box>
            )}

            {/* "Search this area" button - shows when user drags the map */}
            <Button
              variant="contained"
              color="primary"
              size="small"
              icon="search"
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                boxShadow: 3,
                display: 'none' // Will be shown via state when needed
              }}
            >
              {strings?.labels?.search_this_area}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

export default memo(MapSearchModal)
